import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getContacts } from 'Services/invoices';
import { uploadContactToERP } from 'Services/integrations';
import { trackError } from 'Utils/errorMonitoring';
import { RESULTS_PER_PAGE } from 'Utils/constants';
import useIntegrations from './useIntegrations';

export default function useInfiniteContacts(filters = {}) {
    const response = useInfiniteQuery(
        ['contacts', filters],
        async ({ pageParam = 1 }) => {
            const response = await getContacts({
                ...filters,
                page: pageParam,
            });

            return response;
        },
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onError: (err) => trackError(err),
            getNextPageParam: (lastPage, allPages) => {
                const pagesLoaded = allPages.length;
                const totalPages = Math.ceil(lastPage.count / RESULTS_PER_PAGE);
                return pagesLoaded < totalPages ? pagesLoaded + 1 : false;
            },
        },
    );

    const { integrations } = useIntegrations();
    const queryClient = useQueryClient();

    const { mutate: uploadToERP } = useMutation({
        mutationFn: ({ integrationId, contactId }) => uploadContactToERP(integrationId, contactId),
        onMutate: async ({ contactId, integrationId }) => {
            await queryClient.cancelQueries({ queryKey: ['contacts'] });

            const previousPages = queryClient.getQueryData(['contacts', filters]);

            queryClient.setQueryData(['contacts', filters], (old) => {
                if (!old?.pages) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        items: page.items.map((contact) =>
                            contact.id === contactId ? { ...contact, integration_id: integrationId } : contact,
                        ),
                    })),
                };
            });

            return { previousPages };
        },
        onError: (err, _variables, context) => {
            trackError(err);
            if (context?.previousPages) {
                queryClient.setQueryData(['contacts', filters], context.previousPages);
            }
        },
    });

    const formattedPages = response.data?.pages?.map((page) => {
        return {
            ...page,
            items: page.items.map((invoice) => {
                return {
                    ...invoice,
                    integration: invoice.integration_id
                        ? integrations.find((integration) => integration.id === invoice.integration_id)
                        : null,
                };
            }),
        };
    });

    return {
        ...response,
        data: {
            ...response.data,
            pages: formattedPages,
        },
        uploadToERP
    };
}
